import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

export const useInspections = () => {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [inspectionDetails, setInspectionDetails] = useState(null);
  const [choices, setChoices] = useState({ generalAreas: [], skillAreas: [] });
  const [loading, setLoading] = useState(false);

  // Fetch inspection choices (general areas, skill areas)
  const fetchChoices = useCallback(async () => {
    try {
      const url = domainSettings.urls.academics.inspectionChoices;
      const response = await Utilities.fetchWithAuth(
        "GET",
        url,
        null,
        navigate,
      );

      if (response.ok) {
        setChoices(response.data);
        return response.data;
      } else {
        Utilities.showError(response, "Failed to fetch inspection choices");
        return null;
      }
    } catch (error) {
      message.error("Error fetching inspection choices");
      console.error(error);
      return null;
    }
  }, [navigate]);

  // Fetch all inspections with optional filters
  const fetchInspections = useCallback(
    async (params = {}) => {
      setLoading(true);
      try {
        const queryObj = {};
        if (params.classId) queryObj.school_class = params.classId;
        if (params.streamId) queryObj.stream = params.streamId;
        if (params.termId) queryObj.term = params.termId;
        if (params.acadyrId) queryObj.academic_year = params.acadyrId;

        const queryParams = new URLSearchParams(queryObj).toString();
        const url = `${domainSettings.urls.academics.inspections}${queryParams ? "?" + queryParams : ""}`;
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setInspections(
            Array.isArray(response.data)
              ? response.data
              : response.data?.results || [],
          );
        } else {
          Utilities.showError(response, "Failed to fetch inspections");
        }
      } catch (error) {
        message.error("Error fetching inspections");
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Fetch inspection details by ID
  const fetchInspectionDetails = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.inspectionDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          setInspectionDetails(response.data);
          return response.data;
        } else {
          Utilities.showError(response, "Failed to fetch inspection details");
          return null;
        }
      } catch (error) {
        message.error("Error fetching inspection details");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Create a new inspection
  const createInspection = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.inspections,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Inspection created successfully");
          fetchInspections();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to create inspection");
          return null;
        }
      } catch (error) {
        message.error("Error creating inspection");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchInspections],
  );

  // Update an inspection
  const updateInspection = useCallback(
    async (id, data) => {
      setLoading(true);
      try {
        const url = domainSettings.urls.academics.inspectionDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          url,
          data,
          navigate,
        );

        if (response.ok) {
          message.success("Inspection updated successfully");
          fetchInspections();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to update inspection");
          return null;
        }
      } catch (error) {
        message.error("Error updating inspection");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchInspections],
  );

  // Delete inspection
  const deleteInspection = useCallback(
    async (id) => {
      try {
        const url = domainSettings.urls.academics.inspectionDetail.replace(
          ":id",
          id,
        );
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("Inspection deleted successfully");
          fetchInspections();
          return true;
        } else {
          Utilities.showError(response, "Failed to delete inspection");
          return false;
        }
      } catch (error) {
        message.error("Error deleting inspection");
        console.error(error);
        return false;
      }
    },
    [navigate, fetchInspections],
  );

  // Download PDF
  const downloadPdf = useCallback(async (id, filename) => {
    try {
      const url = domainSettings.urls.academics.inspectionDownloadPdf.replace(
        ":id",
        id,
      );
      const accessToken = Utilities.getCookie("access");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename || `inspection_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
        message.success("PDF downloaded successfully");
        return true;
      } else {
        message.error("Failed to download PDF");
        return false;
      }
    } catch (error) {
      message.error("Error downloading PDF");
      console.error(error);
      return false;
    }
  }, []);

  // Regenerate PDF
  const regeneratePdf = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url =
          domainSettings.urls.academics.inspectionRegeneratePdf.replace(
            ":id",
            id,
          );
        const response = await Utilities.fetchWithAuth(
          "GET",
          url,
          null,
          navigate,
        );

        if (response.ok) {
          message.success("PDF regenerated successfully");
          fetchInspections();
          return response.data;
        } else {
          Utilities.showError(response, "Failed to regenerate PDF");
          return null;
        }
      } catch (error) {
        message.error("Error regenerating PDF");
        console.error(error);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate, fetchInspections],
  );

  // Get inspection statistics
  const getStatistics = useCallback(() => {
    return {
      total: inspections.length,
      totalGeneralScore: inspections.reduce(
        (sum, i) => sum + (i.totalGeneralScore || 0),
        0,
      ),
      totalSkillScore: inspections.reduce(
        (sum, i) => sum + (i.totalSkillScore || 0),
        0,
      ),
      averageGeneralScore:
        inspections.length > 0
          ? Math.round(
              inspections.reduce(
                (sum, i) => sum + (i.totalGeneralScore || 0),
                0,
              ) / inspections.length,
            )
          : 0,
      averageSkillScore:
        inspections.length > 0
          ? Math.round(
              inspections.reduce(
                (sum, i) => sum + (i.totalSkillScore || 0),
                0,
              ) / inspections.length,
            )
          : 0,
    };
  }, [inspections]);

  return {
    inspections,
    inspectionDetails,
    choices,
    loading,
    fetchChoices,
    fetchInspections,
    fetchInspectionDetails,
    createInspection,
    updateInspection,
    deleteInspection,
    downloadPdf,
    regeneratePdf,
    getStatistics,
  };
};

export default useInspections;
